import { useCallback, useEffect, useState } from 'react';

interface ReturnValueTypes {
  /**
   * Attach to the description element.
   *
   * RES-1367: a callback ref, rather than the object ref this hook used to be handed, because the
   * description is no longer always on screen -- inside a collapsible block it exists only while
   * the block is open. An object ref says nothing about that: reading `.current` on the way in
   * threw (no element yet), and on the way back it went stale (the element appeared, but nothing
   * re-ran the measurement, so a long description never offered "Read more"). A callback ref fires
   * on both, which is exactly the signal the measurement needs.
   */
  setDescriptionNode: (node: HTMLDivElement | null) => void;
  isOverflowing: boolean;
  isExpanded: boolean;
  onExpand: VoidFunction;
}

/* Two clamped lines of the description; taller than this and there is more to read. */
const CLAMPED_DESCRIPTION_HEIGHT = 44;

export const useAmenities = (isMobileDevice: boolean): ReturnValueTypes => {
  const [descriptionNode, setDescriptionNode] = useState<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const onExpand = useCallback(() => {
    setIsExpanded(expanded => !expanded);
  }, []);

  // No element is not "not overflowing" so much as nothing to say, but it is the same answer: a
  // description that is not on screen has no "Read more" to offer.
  useEffect(() => {
    setIsOverflowing(
      Boolean(isMobileDevice && descriptionNode && descriptionNode.scrollHeight > CLAMPED_DESCRIPTION_HEIGHT),
    );
  }, [descriptionNode, isMobileDevice]);

  return {
    setDescriptionNode,
    isOverflowing,
    isExpanded,
    onExpand,
  };
};
